/**
 * GWT Media Tabs Component
 * One shared slider (1 slide per tab) + peek neighbors.
 * Tabs + arrows always stay in sync.
 */
class GWTMediaTabs extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => this.init());
  }

  init() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.slider = this.querySelector('[data-slider]');
    this.slides = this.slider ? Array.from(this.slider.querySelectorAll('[data-slide]')) : [];

    if (!this.tabs.length || !this.slider || !this.slides.length) return;

    this.defaultIndex = this._clampInt(this.dataset.defaultTab, 0, this.tabs.length - 1);
    this._bindEvents();

    // initial
    this._setActive(this.defaultIndex, { focus: false, center: true, instant: true });
  }

  _bindEvents() {
    // Tab click
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('[role="tab"]');
      if (tab && this.contains(tab)) {
        const idx = this.tabs.indexOf(tab);
        if (idx > -1) this._setActive(idx, { focus: true, center: true });
        return;
      }

      const prev = e.target.closest('[data-slider-prev]');
      const next = e.target.closest('[data-slider-next]');
      if (prev || next) {
        e.preventDefault();
        const dir = prev ? -1 : 1;
        const current = this._getCenteredIndex();
        const target = this._clampInt(current + dir, 0, this.slides.length - 1);
        this._setActive(target, { focus: true, center: true });
      }
    });

    // Keyboard on tablist
    this.tablist?.addEventListener('keydown', (e) => {
      const current = this._activeTabIndex();
      let next = current;

      if (e.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
      else if (e.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = this.tabs.length - 1;
      else return;

      e.preventDefault();
      this._setActive(next, { focus: true, center: true });
    });

    // Keep tab state synced when user drags slider
    this.slider.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(this.slider.__gwtT);
        this.slider.__gwtT = window.setTimeout(() => {
          const idx = this._getCenteredIndex();
          this._setTabState(idx, { focus: false });
          this._markCentered(idx);
        }, 80);
      },
      { passive: true }
    );
  }

  _setActive(index, { focus = true, center = false, instant = false } = {}) {
    index = this._clampInt(index, 0, this.tabs.length - 1);
    this._setTabState(index, { focus });
    this._markCentered(index);

    if (center) this._centerSlide(this.slides[index], instant ? 'auto' : undefined);
  }

  _setTabState(index, { focus = true } = {}) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    if (focus) this.tabs[index]?.focus({ preventScroll: true });
  }

  _markCentered(index) {
    this.slides.forEach((s, i) => s.classList.toggle('is-centered', i === index));
  }

  _centerSlide(slide, mode) {
    if (!slide) return;

    let targetLeft = slide.offsetLeft - (this.slider.clientWidth / 2 - slide.offsetWidth / 2);
    const maxLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    targetLeft = Math.min(maxLeft, Math.max(0, targetLeft));

    const behavior =
      this._prefersReducedMotion() || mode === 'auto' ? 'auto' : 'smooth';

    try {
      this.slider.scrollTo({ left: targetLeft, behavior });
    } catch (e) {
      this.slider.scrollLeft = targetLeft;
    }

    requestAnimationFrame(() => {
      if (Math.abs(this.slider.scrollLeft - targetLeft) > 1) this.slider.scrollLeft = targetLeft;
    });
  }

  _getCenteredIndex() {
    const center = this.slider.scrollLeft + this.slider.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;

    this.slides.forEach((s, i) => {
      const c = s.offsetLeft + s.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });

    return best;
  }

  _activeTabIndex() {
    const idx = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    return idx < 0 ? 0 : idx;
  }

  _clampInt(val, min, max) {
    const n = parseInt(val, 10);
    return Math.min(max, Math.max(min, Number.isNaN(n) ? min : n));
  }

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('gwt-media-tabs')) {
  customElements.define('gwt-media-tabs', GWTMediaTabs);
}
